import React, { useState, useEffect, useMemo } from 'react';
import {
  Users,
  Clock,
  UserCheck,
  Phone,
  RefreshCw,
  Search,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  User
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { getAllAppointments, getPatients, queueCheckin, updateAppointment } from '@/lib/api';

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  mrn?: string;
}

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'in_progress' | 'completed' | 'cancelled';
  visit_type?: string;
  checked_in_at?: string;
}

interface QueueItem extends Appointment {
  patient: Patient | null;
  waitTime?: number;
  queueNumber?: number;
}

export default function QueueManagement() {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsData, patientsData] = await Promise.all([
        getAllAppointments(),
        getPatients({ clinic_id: currentUser?.clinic_id })
      ]);
      if (appointmentsData) setAppointments(appointmentsData);
      if (patientsData?.patients) setPatients(patientsData.patients);
    } catch (err) {
      console.error('Error loading queue:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const getPatient = (patientId: string) => patients.find(p => p.id === patientId) || null;

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const calculateWaitTime = (checkedInAt?: string) => {
    if (!checkedInAt) return 0;
    const now = new Date();
    const checkedIn = new Date(checkedInAt);
    return Math.floor((now.getTime() - checkedIn.getTime()) / 60000); // minutes
  };

  // Build queue with patient data
  const queue: QueueItem[] = useMemo(() => {
    let queueNumber = 1;
    return appointments
      .filter(a => isToday(a.date))
      .map(apt => ({
        ...apt,
        patient: getPatient(apt.patient_id),
        waitTime: apt.status === 'checked_in' ? calculateWaitTime(apt.checked_in_at) : undefined,
        queueNumber: apt.status === 'checked_in' ? queueNumber++ : undefined
      }))
      .sort((a, b) => {
        // Checked in first, then by time
        if (a.status === 'checked_in' && b.status !== 'checked_in') return -1;
        if (a.status !== 'checked_in' && b.status === 'checked_in') return 1;
        return a.time.localeCompare(b.time);
      });
  }, [appointments, patients]);

  // Filter by search
  const filteredQueue = useMemo(() => {
    if (!searchQuery.trim()) return queue;
    const q = searchQuery.toLowerCase();
    return queue.filter(item => 
      item.patient?.name.toLowerCase().includes(q) ||
      item.patient?.phone.includes(q) ||
      item.patient?.mrn?.toLowerCase().includes(q)
    );
  }, [queue, searchQuery]);

  // Separate queues
  const waitingQueue = filteredQueue.filter(a => a.status === 'checked_in');
  const upcomingQueue = filteredQueue.filter(a => a.status === 'scheduled');
  const completedQueue = filteredQueue.filter(a => a.status === 'completed');

  const handleCheckIn = async (appointment: QueueItem) => {
    if (appointment.status !== 'scheduled') return;
    try {
      const entry = await queueCheckin(appointment.id);
      await loadData();

      toast({
        title: "Patient Checked In",
        description: entry
          ? `Token #${entry.tokenNumber} — ${appointment.patient?.name}`
          : `${appointment.patient?.name} has been checked in`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to check in patient",
        variant: "destructive"
      });
    }
  };

  const handleUndoCheckIn = async (appointment: QueueItem) => {
    try {
      await updateAppointment({
        id: appointment.id,
        status: 'scheduled',
        checked_in_at: null
      });
      
      setAppointments(prev => 
        prev.map(a => a.id === appointment.id 
          ? { ...a, status: 'scheduled', checked_in_at: undefined } 
          : a
        )
      );

      toast({
        title: "Check-in Undone",
        description: `${appointment.patient?.name} moved back to scheduled`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to undo check-in",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-warning rounded-lg">
            <Users className="w-5 h-5 text-warning-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Patient Queue</h1>
            <p className="text-sm text-muted-foreground">
              Manage check-ins and waiting patients
            </p>
          </div>
        </div>
        <Button onClick={handleRefresh} variant="outline" disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-warning rounded-full animate-pulse" />
          <span className="font-medium">{waitingQueue.length}</span>
          <span className="text-muted-foreground">Waiting</span>
        </div>
        <div className="border-l border-border" />
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-primary rounded-full" />
          <span className="font-medium">{upcomingQueue.length}</span>
          <span className="text-muted-foreground">Upcoming</span>
        </div>
        <div className="border-l border-border" />
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-success rounded-full" />
          <span className="font-medium">{completedQueue.length}</span>
          <span className="text-muted-foreground">Completed</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, phone, or MRN..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Waiting Queue */}
        <Card className="border-warning/30">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-warning">
              <Clock className="w-5 h-5" />
              Waiting ({waitingQueue.length})
            </h3>
            
            {waitingQueue.length > 0 ? (
              <div className="space-y-3">
                {waitingQueue.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-warning/5 border border-warning/20"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-warning text-warning-foreground rounded-full flex items-center justify-center text-lg font-bold">
                        {item.queueNumber}
                      </div>
                      <div>
                        <p className="font-semibold">{item.patient?.name || 'Unknown'}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{formatTime(item.time)}</span>
                          {item.waitTime !== undefined && item.waitTime > 0 && (
                            <span className={`${item.waitTime > 30 ? 'text-destructive' : 'text-warning'}`}>
                              Waiting {item.waitTime} min
                            </span>
                          )}
                        </div>
                        {item.patient?.phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Phone className="w-3 h-3" />
                            {item.patient.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUndoCheckIn(item)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      Undo
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No patients waiting</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming (Not Checked In) */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-primary">
              <UserCheck className="w-5 h-5" />
              Ready for Check-in ({upcomingQueue.length})
            </h3>
            
            {upcomingQueue.length > 0 ? (
              <div className="space-y-3">
                {upcomingQueue.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold">{item.patient?.name || 'Unknown'}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{formatTime(item.time)}</span>
                          <span>{item.reason || 'General Visit'}</span>
                        </div>
                        {item.patient?.mrn && (
                          <p className="text-xs text-muted-foreground">MRN: {item.patient.mrn}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleCheckIn(item)}
                      className="bg-success hover:bg-success/90"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Check In
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No upcoming appointments</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Completed Today */}
      {completedQueue.length > 0 && (
        <Card className="opacity-75">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-success">
              <CheckCircle className="w-5 h-5" />
              Completed Today ({completedQueue.length})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {completedQueue.slice(0, 8).map((item) => (
                <div
                  key={item.id}
                  className="p-3 rounded-lg bg-success/5 border border-success/20 text-center"
                >
                  <p className="font-medium text-sm">{item.patient?.name}</p>
                  <p className="text-xs text-muted-foreground">{formatTime(item.time)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
